import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ChevronRight, ChevronDown, Search, Sparkles, ClipboardCopy,
  RotateCcw, Package,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Procurement Report — Iter F
 * Answers: "What stickers should I buy today?"
 * Aggregates the WAITING list across every open order (payment_verified,
 * preparing, ready) into a per-code buy list.
 */
export default function ProcurementReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/procurement-report");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load procurement report.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const codes = data?.codes || [];
    const s = search.trim().toUpperCase();
    if (!s) return codes;
    return codes.filter((c) => c.code.includes(s));
  }, [data, search]);

  const toggle = (code) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const copyBuyList = async () => {
    const lines = filtered.map((c) => `${c.code} x${c.qty_needed}`).join("\n");
    if (!lines) return toast.info("Nothing to copy.");
    await navigator.clipboard.writeText(lines);
    toast.success(`Copied ${filtered.length} codes to clipboard.`);
  };

  const totalQty = useMemo(() => filtered.reduce((s, c) => s + c.qty_needed, 0), [filtered]);

  if (loading) return <div className="py-16 text-center"><Loader2 className="animate-spin inline" /> Loading…</div>;

  return (
    <div className="space-y-6" data-testid="procurement-report">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="kicker kicker-red">Procurement</div>
          <h2 className="font-display text-3xl">What should I buy today?</h2>
          <p className="text-sm text-[var(--smoke)] mt-1">
            {filtered.length} unique codes · {totalQty} stickers to source · aggregates the WAITING list from every open order.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2.5 opacity-50" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter codes…"
              className="pl-8 pr-3 py-2 border-2 border-[var(--ink)] rounded-lg text-sm font-mono w-56"
              data-testid="procurement-search"
            />
          </div>
          <button onClick={copyBuyList} className="btn-ghost text-sm" data-testid="procurement-copy">
            <ClipboardCopy size={14} /> Copy buy list
          </button>
          <button onClick={load} className="btn-ghost text-sm" data-testid="procurement-refresh">
            <RotateCcw size={14} /> Refresh
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="p-8 border-2 border-[var(--ink)] rounded-2xl bg-white text-center" data-testid="procurement-empty">
          <Package className="mx-auto opacity-40" size={32} />
          <p className="mt-2 text-sm text-[var(--smoke)]">Nothing to source today — all waiting lists are clear.</p>
        </div>
      ) : (
        <div className="border-2 border-[var(--ink)] rounded-2xl bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest border-b-2 border-[var(--ink)]">
                <th className="px-3 py-2 text-left w-6"></th>
                <th className="px-3 py-2 text-left">Sticker</th>
                <th className="px-3 py-2 text-right">Qty needed</th>
                <th className="px-3 py-2 text-right">Orders waiting</th>
                <th className="px-3 py-2 text-right">Customers</th>
                <th className="px-3 py-2 text-left">Priority</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const open = expanded.has(c.code);
                return (
                  <React.Fragment key={c.code}>
                    <tr
                      className="border-b border-[var(--stone)] hover:bg-[var(--panini-yellow-soft)] cursor-pointer"
                      onClick={() => toggle(c.code)}
                      data-testid={`procurement-row-${c.code}`}
                    >
                      <td className="px-3 py-3">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                      <td className="px-3 py-3">
                        <div className="font-mono font-bold text-lg" data-testid={`procurement-code-${c.code}`}>{c.code}</div>
                        {(c.display_name || c.player_name || c.subject) && (
                          <div className="text-xs text-[var(--smoke)] mt-0.5" data-testid={`procurement-name-${c.code}`}>
                            {c.display_name || c.player_name || c.subject}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <span className="font-display text-2xl">{c.qty_needed}</span>
                      </td>
                      <td className="px-3 py-3 text-right">{c.orders_waiting}</td>
                      <td className="px-3 py-3 text-right">{c.customer_count}</td>
                      <td className="px-3 py-3">
                        <PriorityDistribution dist={c.priority_distribution} />
                      </td>
                    </tr>
                    {open && (
                      <tr className="border-b border-[var(--stone)] bg-[var(--paper)]/60" data-testid={`procurement-detail-${c.code}`}>
                        <td></td>
                        <td colSpan={5} className="px-3 py-3">
                          <OrderList orders={c.orders} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data?.generated_at && (
        <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
          Generated {new Date(data.generated_at).toLocaleString()} · {data.orders_considered} orders considered
        </div>
      )}
    </div>
  );
}

function PriorityDistribution({ dist }) {
  const items = Object.entries(dist || {}).sort((a, b) => b[1] - a[1]);
  if (!items.length) return <span className="opacity-40 text-xs">—</span>;
  const tone = { vip: "bg-red-100 text-red-900 border-red-900", event_pickup: "bg-yellow-100 text-yellow-900 border-yellow-900", rush: "bg-red-100 text-red-900 border-red-900", normal: "bg-neutral-100 text-neutral-800 border-neutral-500" };
  return (
    <div className="flex flex-wrap gap-1">
      {items.map(([k, v]) => (
        <span key={k} className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase font-bold border rounded ${tone[k] || tone.normal}`}>
          {k === "vip" && <Sparkles size={10} />}
          {k.replaceAll("_", " ")} · {v}
        </span>
      ))}
    </div>
  );
}

function OrderList({ orders }) {
  const nav = useNavigate();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {orders.map((o) => (
        <button
          key={o.public_id}
          onClick={() => nav(`/admin/fulfillment/${o.public_id}`)}
          className="text-left p-3 border-2 border-[var(--ink)] rounded-lg bg-white hover:bg-[var(--panini-yellow-soft)]"
          data-testid={`procurement-order-${o.public_id}`}
        >
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono font-bold text-sm">{o.public_id}</span>
            <span className="opacity-60 text-xs">×{o.qty}</span>
          </div>
          <div className="mt-1 flex items-center justify-between gap-3 text-xs">
            <span className="opacity-70">{o.customer_name}</span>
            <span className={`uppercase tracking-widest font-bold text-[10px] ${o.priority === "vip" || o.priority === "rush" ? "text-red-800" : "opacity-60"}`}>
              {o.priority.replaceAll("_", " ")}
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}
